// useMaterialReviewState — the shell's grounding / quality / policy / pane-
// subject cells and the off-screen landed-proposal parking ref. Split out of
// useShellFoundation (size gate) without changing behaviour: called once,
// unconditionally, at the position its cells occupied in the foundation body,
// so the hook-call order is unchanged.
export { useMaterialReviewState };

import type * as Core from "@bilda/core";
import { useRef, useState } from "react";
import type { MaterialRef } from "../../components/Navigation/types.js";
import type { MaterialQuality } from "../../components/QualitySummary/QualitySummary.js";
import type { LandedProposal, LearningContextSummary } from "../ports.js";

const useMaterialReviewState = () => {
    // The audience ("who this is for") the conversation may be grounded on —
    // feeds the SituationProfileChip's "ground to…" picker. The chip's OWN
    // binding (which context, if any, the conversation is currently grounded
    // to) is `situationProfile` below, read via `situationProfileView` and
    // driven by `onGroundConversation`/`onUngroundConversation`.
    const [learningContexts, setLearningContexts] = useState<
        readonly LearningContextSummary[]
    >([]);
    // The chip's own read (Slice 1: durable per-conversation grounding, not
    // client session state). Reloaded on material switch (alongside the rest
    // of the conversation's projections) and after every ground/unground.
    // The material's two-axis quality read (null until evaluated). Both axes are
    // pure server evaluations (no LLM), refreshed on each material switch.
    const [quality, setQuality] = useState<MaterialQuality | null>(null);
    // The material's policy evaluation at the WRITE boundary (advisory register),
    // shown as the PolicyReport inside QualitySummary. Mirrors `quality`: loaded
    // and refreshed at the same triggers, null until first evaluated.
    const [policy, setPolicy] =
        useState<Core.Policy.Evaluate.PolicyEvaluation | null>(null);
    // Per-material policy evaluations, keyed by material id — the source a
    // full-quality report TAB reads (the tab may be active while its material is
    // NOT the active editor, so `policy` above — which follows the active
    // material — cannot serve it). Populated as the active material's policy
    // loads/refreshes (the effect below mirrors `policy` in) and lazily for a
    // quality tab whose material has not been loaded this session. Session-only
    // cache (impure shell), never faked — a missing entry renders a spinner
    // until its lazy load lands.
    const [policyCache, setPolicyCache] = useState<
        ReadonlyMap<string, Core.Policy.Evaluate.PolicyEvaluation>
    >(new Map());
    // What the pane's Review/Grounding projections DESCRIBE — the material
    // whose quality/proposals the load effect last (re-)projected. Kept when
    // the author navigates away from that material, so the lingering read can
    // be subject-labeled in the pane head instead of passing as current
    // (composition contract I6/D7, V7).
    const [paneSubject, setPaneSubject] = useState<MaterialRef | null>(null);
    // §4b: a compose turn's landed proposal, held here when it targets a
    // material whose EditorHost isn't currently mounted (`applyLandedProposal`
    // returned false) — turns are workspace-scoped, not material-scoped
    // (`submitText`'s `target` fallback), so this can genuinely happen. A ref,
    // not state: nothing projects it yet (no rail badge, no replay-on-mount),
    // so there is nothing that needs a re-render when it changes. FLAGGED
    // SHORTCUT — it exists so an off-screen landed proposal is preserved
    // data, not silently dropped, once a consuming surface is built.
    const pendingProposalsByMaterialRef = useRef<
        ReadonlyMap<string, LandedProposal>
    >(new Map());

    return {
        learningContexts,
        setLearningContexts,
        quality,
        setQuality,
        policy,
        setPolicy,
        policyCache,
        setPolicyCache,
        paneSubject,
        setPaneSubject,
        pendingProposalsByMaterialRef,
    };
};
